import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider, Snackbar, Button, Alert } from '@mui/material';

import AppRoutes from './routes/AppRoutes';
import NavigationScroll from './layout/NavigationScroll';
import Theme from './themes';
import { ButtonStyles } from './utils/general';
import { useTheme } from "@mui/styles";
import ScrapingProgressModal from './Components/ScrapingProgressModal';
import LinkedinScrapeProgressModal from './Components/LinkedinScrapeProgressModal';

/**
     * Retrieve the session minutes from local storage.
     * If not available, default to 15 minutes.
     * 
     * @returns {number} The session minutes.
*/
const getSessionMinutes = () => {

    const mins =
        Number(localStorage.getItem("sessionMinutes")) || 15;

    return mins;
};

/**
 * The main application component.
 * 
 * @param {Object} props - The component props.
 * @param {boolean} props.newVersionAvailable - Indicates if a new version of the app is available.
 * @returns {JSX.Element} The rendered application.
 */
const App = ({ newVersionAvailable }) => {
    const Customization = useSelector((state) => state.customization);
    const [showUpdateBanner, setShowUpdateBanner] = useState(newVersionAvailable);
    const [showSessionWarning, setShowSessionWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const theme = useTheme();

    const timeoutRef = useRef(null);
    /**
     * Handle the refresh action.
     * Clear local and session storage, and reload the page.
     */
    const handleRefresh = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload(true);
    };

    /**
     * Handle the session logout action.
     * Clear local and session storage, and navigate to the login page.
     */
    const handleSessionLogout = useCallback(() => {

        localStorage.clear();

        sessionStorage.clear();

        window.location.href = "/login";

    }, []);

    /**
     * Reset the inactivity timer.
     * Clear any existing timeout and set a new timeout based on the session minutes.
     */
    const resetInactivityTimer = useCallback(() => {

        if (timeoutRef.current) {

            clearTimeout(timeoutRef.current);
        }

        const mins = getSessionMinutes();

        timeoutRef.current = setTimeout(() => {

            handleSessionLogout();

        }, mins * 60 * 1000);

    }, [handleSessionLogout]);

    /**
     * Set up event listeners for user activity and reset the inactivity timer.
     * Clean up the event listeners when the component unmounts.
     */
    useEffect(() => {
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart'
        ];

        events.forEach(event =>
            window.addEventListener(event, resetInactivityTimer)
        );

        resetInactivityTimer();

        return () => {
            events.forEach(event =>
                window.removeEventListener(event, resetInactivityTimer)
            );

            if (timeoutRef.current)
                clearTimeout(timeoutRef.current);
        };
    }, [resetInactivityTimer]);

    /**
     * Check if a new version of the app is available and display a banner.
     * Clean up the interval when the component unmounts.
     */
    useEffect(() => {
        const checkInterval = setInterval(() => {
            if (localStorage.getItem('new_version_detected') === 'true') {
                setShowUpdateBanner(true);
            }
        }, 5000);
        return () => clearInterval(checkInterval);
    }, []);

    /**
     * Check if the JWT token is about to expire and display a warning.
     * Clean up the timeout when the component unmounts.
     */
    useEffect(() => {

        const expiry =
            localStorage.getItem("tokenExpiry");

        if (!expiry) return;

        const remaining =
            Number(expiry) - Date.now();

        console.log(
            "Remaining Session Time:",
            remaining
        );

        if (
            remaining <= 60 * 1000 &&
            remaining > 0
        ) {
            setShowSessionWarning(true);
        }

        const timeout = setTimeout(() => {

            console.log("JWT Token Expired");

            handleSessionLogout();

        }, remaining);

        return () => clearTimeout(timeout);

    }, [handleSessionLogout]);

    /**
     * Format the given time in milliseconds to a human-readable format (HHh MMm SSs).
     * 
     * @param {number} ms - The time in milliseconds.
     * @returns {string} The formatted time.
     */
    const formatTime = (ms) => {
        if (!ms) return "Loading...";
        const totalSec = Math.floor(ms / 1000);
        const hrs = Math.floor(totalSec / 3600);
        const mins = Math.floor((totalSec % 3600) / 60);
        const secs = totalSec % 60;
        return `${hrs}h ${mins}m ${secs}s`;
    };

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={Theme(Customization)}>
                <CssBaseline />
                <Snackbar
                    open={showUpdateBanner}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        severity="info"
                        sx={{ width: '100%', backgroundColor: '#fff', color: '#1976d2' }}
                        action={
                            <Button
                                size="small"
                                onClick={handleRefresh}
                                style={ButtonStyles(theme)}
                            >
                                Refresh
                            </Button>
                        }
                    >
                        A new version of the app is available.
                    </Alert>
                </Snackbar>

                <NavigationScroll>
                    <ScrapingProgressModal />
                    <LinkedinScrapeProgressModal />
                    <AppRoutes />
                </NavigationScroll>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default App;